import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Upload, FileText, CheckCircle, Activity, ShieldCheck, X, CreditCard } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const BASE_API = API_URL.endsWith('/api') ? API_URL : (API_URL === '/api' ? '/api' : `${API_URL}/api`);

const ClinicalSubmission = () => {
    const { id } = useParams();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [idProof, setIdProof] = useState(null);
    const [eligibilityMode, setEligibilityMode] = useState(null);
    const [ayushmanCard, setAyushmanCard] = useState(null);
    const [medicalReports, setMedicalReports] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [completed, setCompleted] = useState(false);

    const idInputRef = useRef(null);
    const ayushmanInputRef = useRef(null);
    const medicInputRef = useRef(null);
    const isSubmitting = useRef(false);

    useEffect(() => {
        const fetchLead = async () => {
            try {
                const res = await axios.get(`${BASE_API}/leads/public/${id}`);
                const leadData = res.data.data;
                setLead(leadData);

                if (leadData.hasAyushmanCard) {
                    setEligibilityMode('ayushman');
                } else if (leadData.paymentType && leadData.paymentType.includes('Insurance')) {
                    setEligibilityMode('insurance');
                } else if (leadData.paymentType && leadData.paymentType.includes('Cash')) {
                    setEligibilityMode('cash');
                }

                // 🔒 SECURITY: Enforce One-Time Use for patients, but ALLOW AGENT BYPASS
                // Check if current visitor is an authenticated Agent (Admin/CC)
                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const isAgent = ['admin', 'customer_care', 'cc_head'].includes(storedUser.role);

                if (leadData.isClinicalLinkUsed && !isAgent) {
                    setCompleted(true);
                }
            } catch (err) {
                toast.error(err.response?.data?.message || 'Submission link is invalid or has expired.');
            } finally {
                setLoading(false);
            }
        };
        fetchLead();
    }, [id]);

    const handleIdSelect = (e) => {
        const file = e.target.files[0];
        if (file) setIdProof(file);
        e.target.value = '';
    };

    const handleMedicalSelect = (e) => {
        const files = Array.from(e.target.files);
        setMedicalReports(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeMedical = (idx) => setMedicalReports(prev => prev.filter((_, i) => i !== idx));

    const handleSubmit = async () => {
        if (isSubmitting.current) return;
        if (!eligibilityMode) return toast.error('Please select your payment/eligibility mode.');
        if (eligibilityMode === 'ayushman' && !ayushmanCard) return toast.error('Ayushman card photo is mandatory for this mode.');
        if (!idProof) return toast.error('Identity proof (Aadhaar/PAN) is mandatory.');
        if (medicalReports.length === 0) return toast.error('Please upload at least one medical report.');

        isSubmitting.current = true;
        setUploading(true);
        try {
            // 1. Update Eligibility Mode
            await axios.patch(`${BASE_API}/leads/public/${id}`, { eligibilityMode });

            // 2. Upload ID Proof
            const idFormData = new FormData();
            idFormData.append('documents', idProof);
            await axios.post(`${BASE_API}/upload/public/${id}?type=id_proof`, idFormData);

            // 3. Upload Ayushman Card if applicable
            if (ayushmanCard) {
                const ayuFormData = new FormData();
                ayuFormData.append('documents', ayushmanCard);
                await axios.post(`${BASE_API}/upload/public/${id}?type=ayushman`, ayuFormData);
            }

            // 4. Upload Medical Reports
            const medFormData = new FormData();
            medicalReports.forEach(file => medFormData.append('documents', file));
            await axios.post(`${BASE_API}/upload/public/${id}?type=medical_report`, medFormData);

            setCompleted(true);
            toast.success('Clinical documents and eligibility submitted successfully');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
        } finally {
            isSubmitting.current = false;
            setUploading(false);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-[#143E78] border-t-transparent rounded-full animate-spin"></div>
        </div>
    );

    if (!lead) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
            <div className="max-w-md bg-white p-12 rounded-[40px] shadow-2xl border border-slate-100">
                <Activity className="w-16 h-16 text-red-400 mx-auto mb-6" />
                <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Link Status</h2>
                <p className="text-slate-500 font-bold mt-2">This submission link is invalid or has expired.</p>
                <p className="text-xs text-slate-400 mt-4 font-bold uppercase tracking-widest px-4 leading-relaxed">Please request a fresh clinical protocol link from your Customer Care assistant.</p>
            </div>
        </div>
    );

    if (completed || lead.isClinicalLinkUsed) return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 text-center">
            <div className="max-w-md bg-white p-12 rounded-[40px] shadow-2xl border border-slate-100 animate-fadeIn">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ShieldCheck className="w-10 h-10 text-emerald-600" />
                </div>
                <h2 className="text-3xl font-black text-[#143E78] uppercase italic tracking-tighter">Already Secured</h2>
                <p className="text-slate-500 font-bold mt-4">Thank you, {lead.patientName}.</p>
                <p className="text-xs text-slate-400 mt-4 font-bold px-4 leading-relaxed uppercase tracking-widest">Your clinical protocols are already securely saved and are currently being reviewed by our medical assessment team.</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-white rounded-[40px] shadow-2xl border border-slate-100 overflow-hidden">
                <div className="bg-[#143E78] p-10 text-center">
                    <span className="inline-block px-4 py-1.5 bg-blue-400/20 text-blue-200 text-[10px] font-black rounded-full uppercase tracking-widest mb-4">Patient Intake Portal</span>
                    <h1 className="text-3xl font-black text-white italic tracking-tighter mb-2">Hello, {lead.patientName}</h1>
                    <p className="text-blue-200/80 font-bold text-sm">Action required: Finalize your surgery readiness</p>
                </div>

                <div className="p-10 space-y-10">
                    {/* Eligibility Protocol Section */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-orange-500 text-white flex items-center justify-center font-black text-[10px] italic shadow-lg shadow-orange-100">EP</div>
                            <h3 className="font-black text-slate-800 uppercase text-xs tracking-wider">Eligibility Protocol</h3>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { id: 'cash', label: 'Cash', icon: Activity },
                                { id: 'insurance', label: 'Insurance', icon: ShieldCheck },
                                { id: 'ayushman', label: 'Ayushman', icon: CreditCard }
                            ].map((mode) => (
                                <button
                                    key={mode.id}
                                    onClick={() => setEligibilityMode(mode.id)}
                                    className={`flex flex-col items-center gap-2 p-5 rounded-3xl border-2 transition-all ${eligibilityMode === mode.id
                                        ? 'border-[#143E78] bg-[#143E78] text-white shadow-xl scale-[1.05]'
                                        : 'border-slate-50 bg-slate-50 text-slate-400 hover:border-slate-200'
                                        }`}
                                >
                                    <mode.icon className={`w-5 h-5 ${eligibilityMode === mode.id ? 'text-blue-300' : 'text-slate-300'}`} />
                                    <span className="text-[10px] font-black uppercase tracking-widest">{mode.label}</span>
                                </button>
                            ))}
                        </div>

                        {eligibilityMode === 'ayushman' && (
                            <div className="animate-slideDown">
                                <div
                                    onClick={() => !ayushmanCard && ayushmanInputRef.current.click()}
                                    className={`p-6 rounded-3xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center ${ayushmanCard ? 'border-orange-200 bg-orange-50/30' : 'border-orange-100 bg-orange-50/10 hover:bg-orange-50'
                                        }`}
                                >
                                    {ayushmanCard ? (
                                        <div className="flex items-center justify-between w-full bg-white p-4 rounded-2xl border border-orange-100 shadow-sm">
                                            <div className="flex items-center gap-3">
                                                <CreditCard className="w-6 h-6 text-orange-500" />
                                                <div>
                                                    <p className="text-[10px] font-black text-slate-800 uppercase truncate max-w-[200px]">{ayushmanCard.name}</p>
                                                    <p className="text-[9px] font-bold text-orange-600 uppercase">Ayushman Card Uploaded</p>
                                                </div>
                                            </div>
                                            <button onClick={(e) => { e.stopPropagation(); setAyushmanCard(null); }} className="p-2 text-slate-300 hover:text-red-500 transition-all">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <Upload className="w-8 h-8 text-orange-200 mb-2" />
                                            <p className="text-[10px] font-black text-orange-400 uppercase tracking-widest text-center">Upload Ayushman Card Photo</p>
                                        </>
                                    )}
                                </div>
                                <input type="file" hidden ref={ayushmanInputRef} onChange={(e) => setAyushmanCard(e.target.files[0])} accept="image/*,.pdf" />
                            </div>
                        )}
                    </div>

                    {/* Identity Protocol Section */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-black text-[10px] italic shadow-lg shadow-indigo-100">ID</div>
                            <h3 className="font-black text-slate-800 uppercase text-xs tracking-wider">Identity Protocol (Mandatory)</h3>
                        </div>

                        <div
                            onClick={() => !idProof && idInputRef.current.click()}
                            className={`p-6 rounded-3xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center ${idProof ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100 hover:border-indigo-200 hover:bg-slate-50'
                                }`}
                        >
                            {idProof ? (
                                <div className="flex items-center justify-between w-full bg-white p-4 rounded-2xl border border-emerald-100 shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <ShieldCheck className="w-6 h-6 text-emerald-500" />
                                        <div>
                                            <p className="text-[10px] font-black text-slate-800 uppercase truncate max-w-[200px]">{idProof.name}</p>
                                            <p className="text-[9px] font-bold text-emerald-600 uppercase">Document Secured</p>
                                        </div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); setIdProof(null); }} className="p-2 text-slate-300 hover:text-red-500 transition-all">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <Upload className="w-10 h-10 text-slate-200 mb-2" />
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Upload Aadhaar / PAN Card</p>
                                </>
                            )}
                        </div>
                        <input type="file" hidden ref={idInputRef} onChange={handleIdSelect} accept="image/*,.pdf" />
                    </div>

                    {/* Diagnostic Protocol Section */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-[10px] italic shadow-lg shadow-emerald-100">MD</div>
                            <h3 className="font-black text-slate-800 uppercase text-xs tracking-wider">Upload Medical Reports</h3>
                        </div>

                        <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100 space-y-4">
                            <div className="grid grid-cols-1 gap-2 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                                {medicalReports.map((file, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3.5 bg-white rounded-2xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center gap-3 overflow-hidden">
                                            <FileText className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                                            <span className="text-xs font-black text-slate-700 truncate">{file.name}</span>
                                        </div>
                                        <button onClick={() => removeMedical(idx)} className="p-1.5 text-slate-300 hover:text-red-500 transition-all">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {medicalReports.length === 0 && (
                                    <div className="py-10 text-center opacity-40">
                                        <FileText className="w-8 h-8 mx-auto mb-2" />
                                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Queue is empty</p>
                                    </div>
                                )}
                            </div>
                            <button onClick={() => medicInputRef.current.click()} className="w-full py-4 text-[10px] font-black text-emerald-600 uppercase tracking-widest bg-white hover:bg-emerald-50 rounded-2xl transition-all border-2 border-dashed border-emerald-100">
                                + Add Clinical Reports
                            </button>
                        </div>
                        <input type="file" hidden ref={medicInputRef} onChange={handleMedicalSelect} multiple accept="image/*,.pdf" />
                    </div>

                    <button onClick={handleSubmit} disabled={uploading} className="w-full bg-[#143E78] text-white font-black py-6 rounded-[30px] shadow-2xl shadow-blue-900/20 hover:scale-[1.02] active:scale-95 transition-all uppercase text-sm tracking-widest disabled:opacity-50">
                        {uploading ? 'Initiating Secure Submission...' : 'Confirm & Submit Protocols'}
                    </button>

                    <div className="pt-8 border-t border-slate-50 flex items-center justify-center gap-10 opacity-30">
                        <div className="flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4 text-[#143E78]" />
                            <span className="text-[9px] font-black uppercase text-slate-500 tracking-tighter">Secure Link</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-[#143E78]" />
                            <span className="text-[9px] font-black uppercase text-slate-500 tracking-tighter">Clinical Pipeline</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClinicalSubmission;
